import axios from "axios";
import React from "react";
import {useMonsterCatalog} from "../contexts/MonsterCatalogContext";
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from "@mui/x-data-grid";
import {Autocomplete, Button, Checkbox, FormControlLabel, Grid, Modal, TextField} from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import {Link} from "react-router-dom";
import {CancelRounded, CheckBoxRounded, HelpOutlined} from "@mui/icons-material";
import {MonsterSpecies, MonsterType} from "../../../server/monster";


const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const checkerBoxer = (params: any) => {
    if (params.value == null) {
        return <HelpOutlined htmlColor={"yellow"}/>
    }
    if (params.value) {
        return <CheckBoxRounded htmlColor={"green"}/>
    } else {
        return <CancelRounded htmlColor={"red"}/>
    }

}

const typeColumns: GridColDef[] = [
    {
        field: 'species', headerName: 'Species', headerAlign: "center", valueGetter:(params)=>params.row.species.name
    },
    {
        field: 'name', headerName: 'Type', headerAlign: "center", minWidth: 125,
        renderCell: (params) => <Link to={`/monsters/type/${params.row.id}`}>{params.value}</Link>
    },
    {field: 'description', headerName: 'Description', headerAlign: "center", minWidth: 250},

    {field: 'combat_role', headerName: 'Combat Role', headerAlign: "center", minWidth: 250},
    {field: 'general_use', headerName: 'General Use', headerAlign: "center", renderCell: checkerBoxer, align: "center"}
]

export function MonsterTypeList() {
    const {types, species, refreshTypes} = useMonsterCatalog()
    const [newTypeOpen, setNewTypeOpen] = React.useState(false);
    const [nameError, setNameError] = React.useState<string | null>(null);

    function speciesAutocomplete() {
        let ac = new Array<{ label: string, id: number }>()
        species.forEach((spec) => {
            ac.push({label: spec.name, id: spec.id as number})
        })
        return ac
    }

    const handleOpenNew = () => setNewTypeOpen(true);
    const handleCloseNew = () => { setNewTypeOpen(false); setNameError(null); };

    let newType = {name: "", description: "", species: {}, combat_role: "", general_use: false}

    async function createNewType() {
        try {
            const {data} = await axios.post("/monsters/type", newType)
            refreshTypes()
            handleCloseNew()
            newType = {name: "", description: "", species: {}, combat_role: "", general_use: false}
            return data;
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setNameError(err.response.data?.error ?? 'A monster type with this name already exists.')
            }
        }
    }

    function QuickFilterToolBar() {
        return (
            <GridToolbarContainer>
                <GridToolbarQuickFilter/>
                <Button onClick={handleOpenNew}><PostAddIcon/></Button>
            </GridToolbarContainer>
        )
    }

    return <React.Fragment>
        <Typography variant="h6" component="h6" align="center">Types</Typography>

        <DataGrid slots={{toolbar: QuickFilterToolBar}}
                  autoHeight={true}
                  rows={types}
                  columns={typeColumns}
                  getRowId={(row) => row.id}
                  density={"compact"}
                  initialState={{
                      pagination: {
                          paginationModel: {
                              pageSize: 24,
                          },
                      },
                  }}
        />


        <Modal
            open={newTypeOpen}
            onClose={handleCloseNew}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography id="modal-modal-title" variant="h5" component="h4">
                    Create New Type
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}><Autocomplete
                        disablePortal
                        options={speciesAutocomplete()}
                        sx={{width: 300}}
                        onChange={(ev, newValue) => {
                            function checkId(spec: MonsterSpecies) {
                                if (spec.id == newValue.id) {
                                    return spec as MonsterSpecies
                                }
                            }

                            newType.species = species.find(checkId) as MonsterSpecies
                        }}
                        renderInput={(params) => <TextField {...params} label="Type"/>}
                    /></Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth id='newTypeName' label='Name'
                                   error={!!nameError}
                                   helperText={nameError ?? ''}
                                   onChange={(ev) => {
                                       newType.name = ev.target.value
                                       const dupe = types.some(t => t.name.toLowerCase() === ev.target.value.toLowerCase())
                                       setNameError(dupe ? 'A monster type with this name already exists.' : null)
                                   }}/></Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth id='newTypeDescription' label='Description'
                                   onChange={(ev) => {
                                       newType.description = ev.target.value
                                   }}/>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField fullWidth id='newTypeCombatRole' label='Combat Role'
                                   onChange={(ev) => {
                                       newType.combat_role = ev.target.value
                                   }}/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Checkbox
                            id='newTypeGeneralUser'
                            onChange={(ev) => {
                                newType.general_use = ev.target.checked
                            }}
                            inputProps={{'aria-label': 'controlled'}}
                        />} label="General Use"/>
                    </Grid>
                </Grid>
                <Container align={"center"}><Button onClick={createNewType} disabled={!!nameError}>Submit</Button></Container>
            </Box>
        </Modal>
    </React.Fragment>

}